"use client";

import Link from "next/link";
import Button from "../ui/Button";
import YouTubeFacade from "../ui/YouTubeFacade";
import { useEffect, useState } from "react";
import SponsoredBanners from "../ui/SponsoredBanners";
import {
  getDefaultCommunitySectionConfig,
  type CommunitySectionConfig,
} from "@/lib/community-section-defaults";

const API_BASE = process.env.NEXT_PUBLIC_API_BASE_URL || "http://localhost:4000";

type CommunitySectionProps = {
  /** CMS slug, loads merged copy + video from `/api/pages/community-section`. */
  pageSlug?: string;
  /** CMS sponsored slot (homepage default: `"2"`). */
  sponsoredPosition?: string;
};

export default function CommunitySection({
  pageSlug = "homepage",
  sponsoredPosition,
}: CommunitySectionProps) {
  const [config, setConfig] = useState<CommunitySectionConfig>(() => getDefaultCommunitySectionConfig(pageSlug));

  useEffect(() => {
    setConfig(getDefaultCommunitySectionConfig(pageSlug));
  }, [pageSlug]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const url = `${API_BASE}/api/pages/community-section?pageSlug=${encodeURIComponent(pageSlug)}`;
        const res = await fetch(url, { cache: "no-store" });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && data?.success && data?.config) {
          setConfig(data.config as CommunitySectionConfig);
        }
      } catch {
        // keep defaults
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pageSlug]);

  return (
    <section className="px-4 sm:px-6 lg:px-8" aria-label={config.sectionAriaLabel}>
      <div className="grid grid-cols-1 items-center gap-6 lg:grid-cols-2 lg:gap-12">
        <div className="flex max-sm:order-2 flex-col">
          <h2 className="mb-2 text-h2-mobile font-bold leading-tight text-text-primary sm:mb-6 sm:text-h2-desktop">
            {config.heading}
          </h2>
          <div className="space-y-4 text-body-14-mobile leading-relaxed text-text-secondary sm:text-body-md">
            <p>{config.paragraph1}</p>
            <p>{config.paragraph2}</p>
          </div>
          <div className="mt-8">
            <Button asChild variant="primary" size="lg" className="rounded-xl px-6 py-3">
              <Link href={config.ctaHref}>
                {config.ctaText}
              </Link>
            </Button>
          </div>
        </div>

        <div className="relative aspect-[644/375] max-sm:aspect-square max-sm:order-1 w-full overflow-hidden rounded-xl border border-neutral-10 bg-constant-white p-2 shadow">
          <div className="relative h-full w-full overflow-hidden rounded-xl">
            <YouTubeFacade
              src={config.videoEmbedSrc}
              title={config.videoIframeTitle}
              poster={config.videoPosterSrc}
              posterAlt={config.videoIframeTitle}
              sizes="(max-width: 1024px) 100vw, 50vw"
            />
          </div>
        </div>
      </div>
      {/* No slot named, no banner. Defaulting to the homepage's position 2
          meant any page reusing this block inherited the homepage's banner. */}
      {sponsoredPosition ? (
        <div className="mt-8">
          <SponsoredBanners className="w-full" position={sponsoredPosition} limit={1} />
        </div>
      ) : null}
    </section>
  );
}
